"use client";

import Link from "next/link";
import { motion, useReducedMotion } from "@/lib/motion";
import { scrollRevealViewport } from "@/lib/motion-viewport";
import { ArrowRight } from "@/components/icons";
import { SectionHeader } from "@/components/ui";
import { ROUTES } from "@/lib/constants";
import { ALTERNATIVE_CARDS } from "@/components/sections/editorial/alternatives/archive/data";

const MotionDiv = motion.div;

interface Props {
  currentHref: string;
}

export default function OtherComparisons({ currentHref }: Props) {
  const prefersReducedMotion = useReducedMotion();
  const others = ALTERNATIVE_CARDS.filter((card) => card.href !== currentHref);

  if (others.length === 0) return null;

  return (
    <section
      className="full-bleed band-wash bg-band py-section border-border border-b"
      aria-labelledby="other-comparisons-heading"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <MotionDiv
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
          whileInView={prefersReducedMotion ? {} : { opacity: 1, y: 0 }}
          viewport={scrollRevealViewport}
          transition={{ duration: 0.5 }}
          className="mb-8 max-w-2xl"
        >
          <SectionHeader
            eyebrow="More Comparisons"
            id="other-comparisons-heading"
            heading="Evaluating more than one provider?"
            body="See how Proxidize stacks up against the other proxy providers buyers compare us to."
          />
        </MotionDiv>

        <MotionDiv
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
          whileInView={prefersReducedMotion ? {} : { opacity: 1, y: 0 }}
          viewport={scrollRevealViewport}
          transition={{ duration: 0.5, delay: 0.08 }}
          className="flex flex-wrap items-center gap-3"
        >
          {others.map((card) => (
            <Link
              key={card.href}
              href={card.href}
              className="border-border bg-card text-foreground hover:border-brand/40 hover:bg-brand/5 inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-medium transition-colors"
            >
              vs {card.competitor}
            </Link>
          ))}
          <Link
            href={ROUTES.alternatives}
            className="text-brand hover:text-brand/80 inline-flex items-center gap-1 text-sm font-semibold"
          >
            All comparisons
            <ArrowRight size={14} aria-hidden="true" />
          </Link>
        </MotionDiv>
      </div>
    </section>
  );
}
